import { useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { jwtDecode } from "jwt-decode"

// @mui material components
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"

// Material Dashboard 2 React example components
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// SweetAlert
import { showToast } from "utils/swalToast"

// Services
import {
  fetchSucursales,
  fetchDependencias,
  fetchDependenciaById,
  createOrganizacion,
  updateOrganizacion,
} from "./service"

function OrganizacionAdd() {
  const { id } = useParams()
  const navigate = useNavigate()
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const isEditMode = Boolean(id)

  // State for form data
  const [formData, setFormData] = useState({
    id: null,
    sucursalId: "",
    parentId: "",
    nombre: "",
  })

  // State for dropdowns
  const [sucursales, setSucursales] = useState([])
  const [organizacionesPadre, setDependenciasPadre] = useState([])

  // State for loading
  const [loading, setLoading] = useState(false)
  const [loadingOrganizaciones, setLoadingOrganizaciones] = useState(false)

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadSucursales()
  }, [])

  // Load data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadOrganizacionData()
    }
  }, [isEditMode, id])

  // Load organizaciones padre when sucursal changes
  useEffect(() => {
    if (formData.sucursalId) {
      loadDependenciasPadre()
    } else {
      setDependenciasPadre([])
      setFormData((prev) => ({ ...prev, parentId: "" }))
    }
  }, [formData.sucursalId])

  const loadSucursales = async () => {
    try {
      const data = await fetchSucursales()
      setSucursales(data || [])
    } catch (error) {
      console.error("Error loading sucursales:", error)
    }
  }

  const loadDependenciasPadre = async () => {
    setLoadingOrganizaciones(true)
    try {
      const data = await fetchDependencias({ sucursalId: formData.sucursalId })
      // Filter out current organization if editing
      const filtered = isEditMode ? data.filter((org) => org.id !== parseInt(id)) : data
      setDependenciasPadre(filtered || [])
    } catch (error) {
      console.error("Error loading organizaciones padre:", error)
    } finally {
      setLoadingOrganizaciones(false)
    }
  }

  const loadOrganizacionData = async () => {
    setLoading(true)
    try {
      const data = await fetchDependenciaById(id)
      setFormData({
        id: data.id,
        sucursalId: data.sucursalId || "",
        parentId: data.parentId || "",
        nombre: data.nombre || "",
      })
    } catch (error) {
      console.error("Error loading dependencia:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validations
    if (!formData.sucursalId) {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor seleccione una sucursal" })
      return
    }

    if (!formData.nombre || formData.nombre.trim() === "") {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor ingrese el nombre de la dependencia" })
      return
    }

    setLoading(true)
    try {
      const dataToSend = {
        id: formData.id,
        sucursalId: formData.sucursalId,
        parentId: formData.parentId || null,
        nombre: formData.nombre.trim(),
      }

      if (isEditMode) {
        await updateOrganizacion(id, dataToSend)
      } else {
        await createOrganizacion(dataToSend)
      }

      navigate("/asistencia/dependencias")
    } catch (error) {
      console.error("Error saving dependencia:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h5">
                  {isEditMode ? "Editar dependencia" : "Agregar dependencia"}
                </MDTypography>
              </MDBox>
              <MDBox pt={2} px={2} component="form" role="form" onSubmit={handleSubmit}>
                {/* Sucursal */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Sucursal</InputLabel>
                    <Select
                      label="Sucursal"
                      name="sucursalId"
                      value={formData.sucursalId}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione una sucursal</MenuItem>
                      {sucursales.map((suc) => (
                        <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                          {suc.descripcion}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Dependencia Padre */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Dependencia Padre (Opcional)</InputLabel>
                    <Select
                      label="Dependencia Padre (Opcional)"
                      name="parentId"
                      value={formData.parentId}
                      onChange={handleInputChange}
                      disabled={!formData.sucursalId || loadingOrganizaciones || loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Ninguna</MenuItem>
                      {organizacionesPadre.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Nombre */}
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    name="nombre"
                    label="Nombre de la Dependencia"
                    fullWidth
                    required
                    value={formData.nombre}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </MDBox>

                {/* Botones */}
                <MDBox mb={2} sx={{ textAlign: "center" }}>
                  <Link to="/asistencia/dependencias">
                    <MDButton variant="outlined" color="info" sx={{ mr: 1, width: 90 }} disabled={loading}>
                      Cancelar
                    </MDButton>
                  </Link>
                  <MDButton type="submit" variant="gradient" color="info" sx={{ ml: 1, width: 90 }} disabled={loading}>
                    {loading ? "..." : isEditMode ? "Actualizar" : "Agregar"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default OrganizacionAdd
